import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { Bot, MessageSquare, CheckCircle, AlertCircle, Save, Plus, Trash2, GripVertical, Upload, Camera } from 'lucide-react';

function Notification({ message, type, onClose }) {
    useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
    const colors = type === 'success' ? 'bg-green-50 border-green-400 text-green-800' : 'bg-red-50 border-red-400 text-red-800';
    return (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg border-l-4 shadow-lg ${colors} flex items-center gap-2`}>
            {type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {message}
        </div>
    );
}

const DEFAULT_STEPS = [
    "introduction",
    "parcours_academique",
    "competences_techniques",
    "projet_phare_1",
    "projet_phare_2",
    "soft_skills",
    "motivation_poste",
    "conclusion_contact"
];

export default function PersonaPage() {
    const [persona, setPersona] = useState(null);
    const [form, setForm] = useState({
        name: 'Claud-IA Touré',
        personality: '',
        tone: 'professionnel mais chaleureux',
        greeting_template: '',
        presentation_plan: DEFAULT_STEPS,
        custom_system_prompt: '',
    });
    const [loading, setLoading] = useState(true);
    const [notif, setNotif] = useState(null);
    const [newStep, setNewStep] = useState('');
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [uploading, setUploading] = useState(false);

    // Convert presentation_plan from backend format to string array
    const planToArray = (plan) => {
        let steps;
        if (Array.isArray(plan)) {
            steps = plan;
        } else if (plan && typeof plan === 'object') {
            if (Array.isArray(plan.steps)) {
                steps = plan.steps;
            } else {
                const keys = Object.keys(plan);
                return keys.length > 0 ? keys : DEFAULT_STEPS;
            }
        } else {
            return DEFAULT_STEPS;
        }
        // Convert object steps to string labels
        return steps.map(s => typeof s === 'string' ? s : (s.title || s.id || JSON.stringify(s)));
    };

    useEffect(() => {
        api.get('/persona').then(({ data }) => {
            setPersona(data);
            setAvatarUrl(data.avatar_url || null);
            setForm({
                name: data.name || 'Claud-IA Touré',
                personality: data.personality || '',
                tone: data.tone || 'professionnel mais chaleureux',
                greeting_template: data.greeting_template || '',
                presentation_plan: planToArray(data.presentation_plan),
                custom_system_prompt: data.custom_system_prompt || '',
            });
        }).catch(() => {
            // Persona not created yet — keep defaults
        }).finally(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        try {
            // Backend expects presentation_plan as dict with "steps" key
            const payload = {
                ...form,
                presentation_plan: { steps: form.presentation_plan },
            };
            await api.put('/persona', payload);
            setPersona({ ...persona, ...payload });
            setNotif({ type: 'success', message: 'Persona sauvegardée !' });
        } catch (err) {
            setNotif({ type: 'error', message: err.response?.data?.detail || 'Erreur' });
        }
    };

    const handleAvatarUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
        if (!allowed.includes(file.type)) {
            setNotif({ type: 'error', message: 'Format non supporté. Utilisez PNG, JPG, GIF ou WebP.' });
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setNotif({ type: 'error', message: 'Fichier trop volumineux (max 10 Mo).' });
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post('/persona/avatar', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setAvatarUrl(data.avatar_url);
            setNotif({ type: 'success', message: 'Avatar mis à jour !' });
        } catch (err) {
            setNotif({ type: 'error', message: err.response?.data?.detail || 'Erreur upload avatar' });
        } finally {
            setUploading(false);
        }
    };

    const addStep = () => {
        if (!newStep.trim()) return;
        setForm({ ...form, presentation_plan: [...form.presentation_plan, newStep.trim()] });
        setNewStep('');
    };

    const removeStep = (index) => {
        setForm({
            ...form,
            presentation_plan: form.presentation_plan.filter((_, i) => i !== index),
        });
    };

    const moveStep = (index, direction) => {
        const steps = [...form.presentation_plan];
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= steps.length) return;
        [steps[index], steps[newIndex]] = [steps[newIndex], steps[index]];
        setForm({ ...form, presentation_plan: steps });
    };

    if (loading) return <div className="text-gray-500">Chargement...</div>;

    return (
        <div className="max-w-3xl">
            {notif && <Notification {...notif} onClose={() => setNotif(null)} />}

            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Persona IA</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Configurez la personnalité et le comportement de votre assistant IA
                    </p>
                </div>
                <button onClick={handleSave}
                    className="flex items-center gap-2 bg-[#5764F4] hover:bg-[#4654E3] text-white px-4 py-2 rounded-lg font-medium transition-colors">
                    <Save className="w-4 h-4" /> Sauvegarder
                </button>
            </div>

            {/* Identity */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                    <Bot className="w-5 h-5 text-[#5764F4]" />
                    <h2 className="text-lg font-semibold text-gray-900">Identité</h2>
                </div>

                {/* Avatar Upload */}
                <div className="flex items-center gap-6 mb-6">
                    <div className="relative group">
                        <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-[#5764F4] to-purple-500 flex items-center justify-center">
                            {avatarUrl ? (
                                <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                            ) : (
                                <Bot className="w-8 h-8 text-white" />
                            )}
                        </div>
                        <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                            <Camera className="w-5 h-5 text-white" />
                            <input type="file" accept="image/png,image/jpeg,image/gif,image/webp"
                                onChange={handleAvatarUpload} className="hidden" />
                        </label>
                        {uploading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-full">
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        )}
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-700">Avatar de la persona</p>
                        <p className="text-xs text-gray-400 mt-0.5">PNG, JPG, GIF ou WebP (max 10 Mo)</p>
                        <label className="inline-flex items-center gap-1 mt-2 text-xs text-[#5764F4] hover:text-[#4654E3] cursor-pointer font-medium">
                            <Upload className="w-3.5 h-3.5" /> Changer l'avatar
                            <input type="file" accept="image/png,image/jpeg,image/gif,image/webp"
                                onChange={handleAvatarUpload} className="hidden" />
                        </label>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la Persona</label>
                        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm"
                            placeholder="Claud-IA Touré" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Ton</label>
                        <select value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm bg-white">
                            <option value="professionnel mais chaleureux">Professionnel mais chaleureux</option>
                            <option value="formel et concis">Formel et concis</option>
                            <option value="décontracté et amical">Décontracté et amical</option>
                            <option value="enthousiaste et dynamique">Enthousiaste et dynamique</option>
                        </select>
                    </div>
                </div>
                <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Personnalité</label>
                    <textarea value={form.personality} onChange={(e) => setForm({ ...form, personality: e.target.value })} rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm"
                        placeholder="Décrivez la personnalité de l'IA : ses traits, sa façon de s'exprimer, ses valeurs..." />
                </div>
            </div>

            {/* Greeting */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                    <MessageSquare className="w-5 h-5 text-green-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Message d'accueil</h2>
                </div>
                <p className="text-xs text-gray-400 mb-2">
                    Utilisez {'{{recruiter_name}}'}, {'{{company}}'}, {'{{persona_name}}'} comme variables
                </p>
                <textarea value={form.greeting_template} onChange={(e) => setForm({ ...form, greeting_template: e.target.value })} rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm"
                    placeholder="Bonjour {{recruiter_name}} ! Je suis {{persona_name}}, l'assistante IA d'Abdoulaye. Bienvenue !" />
            </div>

            {/* Presentation plan */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Plan de présentation</h2>
                <p className="text-xs text-gray-400 mb-4">
                    Définissez les étapes de la présentation automatique. L'IA suivra cet ordre pour présenter le profil.
                </p>

                <div className="space-y-2 mb-4">
                    {form.presentation_plan.map((step, i) => (
                        <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                            <span className="text-xs text-gray-400 font-mono w-6">{i + 1}.</span>
                            <span className="flex-1 text-sm text-gray-700">{step}</span>
                            <div className="flex items-center gap-1">
                                <button onClick={() => moveStep(i, -1)} disabled={i === 0}
                                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30" title="Monter">
                                    ↑
                                </button>
                                <button onClick={() => moveStep(i, 1)} disabled={i === form.presentation_plan.length - 1}
                                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30" title="Descendre">
                                    ↓
                                </button>
                                <button onClick={() => removeStep(i)}
                                    className="p-1 text-gray-400 hover:text-red-500" title="Supprimer">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex gap-2">
                    <input value={newStep} onChange={(e) => setNewStep(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addStep()}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm"
                        placeholder="Nom de l'étape (ex: experiences_internationales)" />
                    <button onClick={addStep}
                        className="flex items-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors">
                        <Plus className="w-4 h-4" /> Ajouter
                    </button>
                </div>
            </div>

            {/* Advanced: custom system prompt */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Prompt Système personnalisé</h2>
                <p className="text-xs text-gray-400 mb-3">
                    Définissez le comportement et la personnalité de l'IA.
                    Variables disponibles : <code className="bg-gray-100 px-1 rounded">{'{persona_name}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{profile_name}'}</code>.
                    Ce prompt contrôle comment l'IA se comporte (1ère/3ème personne, tutoiement/vouvoiement, ton, etc.).
                </p>
                <textarea value={form.custom_system_prompt}
                    onChange={(e) => setForm({ ...form, custom_system_prompt: e.target.value })} rows={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] text-sm font-mono text-xs"
                    placeholder="Tu es {persona_name}, le double numérique de {profile_name}..." />
            </div>
        </div>
    );
}
